<template>
  <div class="merchant-price-page">
    <basic-container>
      <avue-crud
        ref="crud"
        :option="tableOption"
        :data="tableData"
        :page="page"
        :table-loading="tableLoading"
        @on-load="handleList"
        @search-change="handleSearch"
        @search-reset="handleReset"
        @selection-change="handleSelectionChange"
        @refresh-change="handleList"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      >
        <template slot="menuLeft">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">
            新建价目表
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            size="small"
            plain
            :disabled="!selectedRows.length"
            @click="handleBatchDelete"
          >
            批量删除
          </el-button>
        </template>

        <template slot="isFallback" slot-scope="scope">
          <el-tag v-if="scope.row.isFallback === 'YES'" type="warning" size="small">兜底</el-tag>
          <span v-else>普通</span>
        </template>
        <template slot="basePrice" slot-scope="scope">
          <template v-if="scope.row.basePrice != null">
            <span style="color: #e6a23c; font-weight: 600">{{ scope.row.basePrice }}</span>
            <span style="color: #909399; font-size: 12px"> 元/㎡</span>
          </template>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
        <template slot="threshold" slot-scope="scope">
          <span v-if="scope.row.threshold != null">{{ scope.row.threshold }}</span>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
        <template slot="effectiveStartTime" slot-scope="scope">
          <span v-if="scope.row.effectiveStartTime">{{ displayTime(scope.row.effectiveStartTime) }}</span>
          <span v-else style="color: #c0c4cc">立即生效</span>
        </template>
        <template slot="effectiveEndTime" slot-scope="scope">
          <span v-if="scope.row.effectiveEndTime">{{ displayTime(scope.row.effectiveEndTime) }}</span>
          <span v-else style="color: #c0c4cc">长期有效</span>
        </template>
        <template slot="effectiveStatus" slot-scope="scope">
          <el-tag :type="scope.row.effectiveStatusType" size="small">
            {{ scope.row.effectiveStatus }}
          </el-tag>
        </template>
        <template slot="status" slot-scope="scope">
          <el-tag :type="scope.row.status === 'ENABLE' ? 'success' : 'info'" size="small">
            {{ scope.row.status === 'ENABLE' ? '启用' : '停用' }}
          </el-tag>
        </template>
        <template slot="createTime" slot-scope="scope">
          <span v-if="scope.row.createTime">{{ displayTime(scope.row.createTime) }}</span>
          <span v-else style="color: #c0c4cc">-</span>
        </template>

        <template slot-scope="scope" slot="menu">
          <el-button type="text" icon="el-icon-setting" size="small" @click="handleMerchantPrice(scope.row)">
            商户价
          </el-button>
          <el-button type="text" icon="el-icon-tickets" size="small" @click="handleAdjustTask()">
            调价任务
          </el-button>
          <el-button type="text" icon="el-icon-edit" size="small" @click="handleEdit(scope.row)">
            编辑
          </el-button>
          <el-button
            type="text"
            :icon="scope.row.status === 'ENABLE' ? 'el-icon-close' : 'el-icon-check'"
            size="small"
            @click="handleToggleStatus(scope.row)"
          >
            {{ scope.row.status === 'ENABLE' ? '停用' : '启用' }}
          </el-button>
          <el-button
            type="text"
            icon="el-icon-delete"
            size="small"
            style="color: #f56c6c"
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
        </template>
      </avue-crud>
    </basic-container>

    <!-- 价目表新增/编辑弹窗 -->
    <PriceBookFormDialog
      :visible.sync="formDialogVisible"
      :mode="formDialogMode"
      :price-book="currentRow"
      @submit-success="handleList"
    />

    <!-- 商户价配置弹窗 -->
    <MerchantPriceDrawer
      :visible.sync="merchantDialogVisible"
      :price-book="currentRow"
    />
  </div>
</template>

<script>
import { priceBookTableOption } from "@/const/work/waterProof/merchantPriceOption";
import {
  findPriceBookPage,
  updatePriceBook,
  deletePriceBook,
} from "@/api/work/waterProof/merchantPrice";
import { formatterTime } from "@/util/util";
import PriceBookFormDialog from "./components/PriceBookFormDialog.vue";
import MerchantPriceDrawer from "./components/MerchantPriceDrawer.vue";

export default {
  name: "merchantPrice",
  components: {
    PriceBookFormDialog,
    MerchantPriceDrawer,
  },
  data() {
    return {
      tableOption: priceBookTableOption,
      tableData: [],
      tableLoading: false,
      selectedRows: [],
      page: { total: 0, currentPage: 1, pageSize: 10 },
      searchParams: {},
      formDialogVisible: false,
      formDialogMode: "add",
      merchantDialogVisible: false,
      currentRow: null,
    };
  },
  methods: {
    formatTime: formatterTime,
    parseData(res) {
      return typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
    },
    // 时间转时间戳：兼容时间戳与 "yyyy-MM-dd HH:mm:ss" 字符串（字符串 - 换 / 兼容 Safari 解析）
    toTime(v) {
      if (v == null || v === "") return null;
      if (typeof v === "number") return v;
      const s = String(v);
      if (/^\d+$/.test(s)) return Number(s);
      const t = new Date(s.replace(/-/g, "/")).getTime();
      return isNaN(t) ? null : t;
    },
    // 表格展示：后端返回展示格式字符串时直接显示，时间戳则走 formatterTime
    displayTime(v) {
      if (v == null || v === "") return "";
      if (typeof v === "string" && !/^\d+$/.test(v)) return v;
      return this.formatTime(v);
    },
    // 生效状态：已停用 / 未生效 / 生效中 / 已失效
    computeEffectiveStatus(row) {
      if (row.status === "DISABLE") return { text: "已停用", type: "info" };
      const now = Date.now();
      const start = this.toTime(row.effectiveStartTime);
      const end = this.toTime(row.effectiveEndTime);
      if (start && now < start) {
        return { text: "未生效", type: "warning" };
      }
      if (end && now > end) {
        return { text: "已失效", type: "danger" };
      }
      return { text: "生效中", type: "success" };
    },
    buildParams() {
      // 接口 page 从 1 开始
      const params = {
        page: this.page.currentPage,
        size: this.page.pageSize,
      };
      Object.keys(this.searchParams).forEach((k) => {
        const v = this.searchParams[k];
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      return params;
    },
    handleList() {
      this.tableLoading = true;
      findPriceBookPage(this.buildParams())
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            const rows = data.rows || [];
            rows.forEach((r) => {
              const s = this.computeEffectiveStatus(r);
              this.$set(r, "effectiveStatus", s.text);
              this.$set(r, "effectiveStatusType", s.type);
            });
            this.tableData = rows;
            this.page.total = data.total || 0;
          } else {
            this.$message.error(res.message || "加载失败");
          }
        })
        .catch((err) => {
          console.log("handleList error:", err);
          this.$message.error("加载异常");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    handleSearch(params) {
      this.searchParams = params || {};
      this.page.currentPage = 1;
      this.handleList();
    },
    handleReset() {
      this.searchParams = {};
      this.page.currentPage = 1;
      this.handleList();
    },
    handlePageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    handleSizeChange(size) {
      this.page.pageSize = size;
      this.page.currentPage = 1;
      this.handleList();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection || [];
    },
    handleAdd() {
      this.formDialogMode = "add";
      this.currentRow = null;
      this.formDialogVisible = true;
    },
    handleEdit(row) {
      this.formDialogMode = "edit";
      this.currentRow = row;
      this.formDialogVisible = true;
    },
    handleMerchantPrice(row) {
      this.currentRow = row;
      this.merchantDialogVisible = true;
    },
    // 跳转调价管理页（隐藏路由，页内自行选价盘）
    handleAdjustTask() {
      this.$router.push("/work/waterProof/merchantPrice/adjustTask");
    },
    // 停用/启用：全量带回可编辑字段，仅切换 status
    handleToggleStatus(row) {
      const target = row.status === "ENABLE" ? "DISABLE" : "ENABLE";
      const actionText = target === "ENABLE" ? "启用" : "停用";
      this.$confirm(`确定${actionText}价目表「${row.name}」吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          updatePriceBook({
            id: row.id,
            name: row.name,
            priority: row.priority,
            isFallback: row.isFallback,
            basePrice: row.basePrice,
            threshold: row.threshold,
            subsidyPrice: row.subsidyPrice,
            effectiveStartTime: row.effectiveStartTime,
            effectiveEndTime: row.effectiveEndTime,
            status: target,
          })
            .then((res) => {
              if (res.code === "200") {
                this.$message.success(`${actionText}成功`);
                this.handleList();
              } else {
                this.$message.error(res.message || `${actionText}失败`);
              }
            })
            .catch((err) => {
              console.log("handleToggleStatus error:", err);
              this.$message.error(`${actionText}异常`);
            });
        })
        .catch(() => {});
    },
    handleDelete(row) {
      this.$confirm(
        `确定删除价目表「${row.name}」吗？删除后将级联删除该价盘下全部商户价，且不可恢复！`,
        "警告",
        { confirmButtonText: "确定", cancelButtonText: "取消", type: "warning" }
      )
        .then(() => this.doDelete(row.id))
        .catch(() => {});
    },
    handleBatchDelete() {
      if (!this.selectedRows.length) {
        this.$message.warning("请先选择要删除的项");
        return;
      }
      this.$confirm(
        `确定删除选中的 ${this.selectedRows.length} 张价目表吗？删除后将级联删除其下全部商户价，且不可恢复！`,
        "警告",
        { confirmButtonText: "确定", cancelButtonText: "取消", type: "warning" }
      )
        .then(() => this.doDelete(this.selectedRows.map((r) => r.id).join(",")))
        .catch(() => {});
    },
    doDelete(ids) {
      deletePriceBook(ids)
        .then((res) => {
          if (res.code === "200") {
            this.$message.success("删除成功");
            this.selectedRows = [];
            this.page.currentPage = 1;
            this.handleList();
          } else {
            this.$message.error(res.message || "删除失败");
          }
        })
        .catch((err) => {
          console.log("doDelete error:", err);
          this.$message.error("删除异常");
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.merchant-price-page {
  ::v-deep .el-tabs__header {
    margin-bottom: 8px;
  }
}
</style>
